import {
  axiosUnauthorized,
  axiosAuthorized,
  tokenManager,
} from "../utils/axios";
import config from "../config/config";

// Types for API requests and responses
export interface RegisterRequest {
  email: string;
  password: string;
  name: string;
  phone: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface VerifyForgotPasswordRequest {
  forgotToken: string;
  newpassword: string;
}

export interface ChangePasswordRequest {
  oldPassword: string;
  newPassword: string;
}

export interface UpdateProfileRequest {
  name?: string;
  email?: string;
  phone?: string;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface VerifyOtpRequest {
  idToken: string;
  email?: string;
}

export interface User {
  id: number;
  email: string;
  name: string;
  phone: string;
  createdAt: string;
  updatedAt: string;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    user: User;
    access_token: string;
    refresh_token: string;
  };
}

export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

export interface RefreshTokenResponse {
  success: boolean;
  data: {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
  };
  message: string;
}

class AuthService {
  // Use a relative path, as the base is set in axios config
  private readonly authEndpoint = config.ENDPOINTS.AUTH;
  private readonly profileEndpoint = config.ENDPOINTS.PROFILE;

  /**
   * Register a new user
   */
  async register(userData: RegisterRequest): Promise<ApiResponse> {
    try {
      const response = await axiosUnauthorized.post(
        `${this.authEndpoint}/register`,
        userData
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Login user
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await axiosUnauthorized.post(
        `${this.authEndpoint}/login`,
        credentials
      );

      const { data } = response.data;

      // Store tokens and user data
      tokenManager.setTokens(data.access_token, data.refresh_token);
      tokenManager.setUser(data.user);

      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Verify a Firebase phone-auth idToken and log in (creating the user if needed)
   */
  async verifyOtp(data: VerifyOtpRequest): Promise<LoginResponse> {
    try {
      const response = await axiosUnauthorized.post(
        `${this.authEndpoint}/verifyOtp`,
        data
      );

      const { data: responseData } = response.data;

      tokenManager.setTokens(
        responseData.access_token,
        responseData.refresh_token
      );
      tokenManager.setUser(responseData.user);

      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      // Clear tokens from storage
      tokenManager.clearTokens();

      // Optionally call logout endpoint if it exists
      // await axiosAuthorized.post(`${this.authEndpoint}/logout`);
    } catch (error: any) {
      // Even if logout fails, clear tokens locally
      tokenManager.clearTokens();
      throw this.handleError(error);
    }
  }

  /**
   * Send forgot password email
   */
  async forgotPassword(email: ForgotPasswordRequest): Promise<ApiResponse> {
    try {
      const response = await axiosUnauthorized.post(
        `${this.authEndpoint}/forgotPassword`,
        email
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Verify forgot password token and reset password
   */
  async verifyForgotPassword(
    data: VerifyForgotPasswordRequest
  ): Promise<ApiResponse> {
    try {
      const response = await axiosUnauthorized.post(
        `${this.authEndpoint}/verifyForgotPassword`,
        data
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Change user password
   */
  async changePassword(
    userId: number,
    passwordData: ChangePasswordRequest
  ): Promise<ApiResponse> {
    try {
      const response = await axiosAuthorized.post(
        `${this.authEndpoint}/change-password/${userId}`,
        passwordData
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Fetch the logged-in user's own profile (always self - see
   * fusion_backend's getMyProfile, which ignores any client-supplied id).
   */
  async getProfile(): Promise<ApiResponse<User>> {
    try {
      const response = await axiosAuthorized.get(
        `${this.profileEndpoint}/get-profile`
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Update the logged-in user's own name/email/phone.
   */
  async updateProfile(
    profileData: UpdateProfileRequest
  ): Promise<ApiResponse<User>> {
    try {
      const response = await axiosAuthorized.post(
        `${this.profileEndpoint}/edit-profile`,
        profileData
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Refresh access token
   */
  async refreshToken(
    refreshTokenData?: RefreshTokenRequest
  ): Promise<RefreshTokenResponse> {
    try {
      const refreshToken =
        refreshTokenData?.refreshToken || tokenManager.getRefreshToken();

      if (!refreshToken) {
        throw new Error("No refresh token available");
      }

      const response = await axiosUnauthorized.post(
        `${this.authEndpoint}/refresh-token`,
        { refreshToken }
      );

      const { data } = response.data;

      // Update stored tokens
      tokenManager.setTokens(data.accessToken, data.refreshToken);

      return response.data;
    } catch (error: any) {
      // If refresh fails, clear tokens and redirect to login
      tokenManager.clearTokens();
      throw this.handleError(error);
    }
  }

  /**
   * Get current user from storage
   */
  getCurrentUser(): User | null {
    return tokenManager.getUser();
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = tokenManager.getAccessToken();
    const user = tokenManager.getUser();
    return !!(token && user);
  }

  /**
   * Get current access token
   */
  getAccessToken(): string | null {
    return tokenManager.getAccessToken();
  }

  /**
   * Get current refresh token
   */
  getRefreshToken(): string | null {
    return tokenManager.getRefreshToken();
  }

  /**
   * Handle API errors
   */
  private handleError(error: any): Error {
    if (error.response) {
      // Server responded with error status
      const message =
        error.response.data?.message ||
        error.response.data?.error ||
        "An error occurred";
      return new Error(message);
    } else if (error.request) {
      // Request was made but no response received
      return new Error("Network error. Please check your connection.");
    } else {
      // Something else happened
      return new Error(error.message || "An unexpected error occurred");
    }
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;
